import {
  KdsCallout,
  KdsDropfile,
  KdsTextField,
} from "@kazaar-platform/design-system";
import { RiInformationLine } from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { useContext } from "react";
import { Controller } from "react-hook-form";

import { FileContext } from "@/components/FileUploadAsync/FileUploadAsync.context";
import { SIZE_20M } from "@/constants/fileSize.constants";
import { getDropfileErrorMessage } from "@/helpers/forms";
import { useDirectUpload } from "@/hooks/useDirectUpload";
import { type ModalPurchaseOrderFormContentProps } from "./ModalPurchaseOrderFormContent.type";

export const ModalPurchaseOrderFormContent = (
  props: ModalPurchaseOrderFormContentProps
) => {
  const { t } = useTranslate();

  // Hooks
  const { pushFile, setFileUploadState } = useContext(FileContext);
  const { uploadFile, createFileUploadUrl } = useDirectUpload();

  const handleUpload = async (file: File) => {
    const uploadDetails = await createFileUploadUrl("order.orderFile", file);
    const newSignedId = uploadDetails?.fileSignedId;

    pushFile(newSignedId, {
      fileName: file.name,
      size: file.size,
      contentType: file.type,
    });

    await uploadFile(file, uploadDetails);

    setFileUploadState(newSignedId, "uploaded");

    props.methods.setValue("purchaseOrderFileSignedId", newSignedId);
  };

  return (
    <div className="flex flex-col gap-sp-md">
      <KdsCallout
        iconLeft={RiInformationLine}
        state="info"
        title={t("cardOrder.modalAddPurchaseOrder.callout")}
      />

      <Controller
        control={props.methods.control}
        name="orderNumber"
        render={({ field, fieldState }) => (
          <KdsTextField
            errorMessage={fieldState.error?.message}
            hasError={!!fieldState.error?.message}
            label={t("cardOrder.modalAddPurchaseOrder.inputLabel")}
            onChange={field.onChange}
            placeholder={t("cardOrder.modalAddPurchaseOrder.inputPlaceholder")}
            required
            value={field.value}
          />
        )}
      />

      <Controller
        name="purchaseOrderFileSignedId"
        render={({ field, fieldState }) => (
          <KdsDropfile
            errorMessage={getDropfileErrorMessage(fieldState.error?.message)}
            hasError={!!fieldState.error}
            isDeletable={!!field.value}
            isLoading={props.isLoading}
            maxSize={SIZE_20M}
            onDelete={() => field.onChange(undefined)}
            onUpload={(file) => void handleUpload(file)}
            onUploadError={() =>
              props.methods.setValue("purchaseOrderFileSignedId", "")
            }
            onUploadStart={() => {
              props.setIsLoading(true);
              props.methods.clearErrors("purchaseOrderFileSignedId");
            }}
            onUploadSuccess={() => props.setIsLoading(false)}
            placeholderDescription={t(
              "cardOrder.modalAddPurchaseOrder.dropfilePlaceholderDescription"
            )}
            placeholderTitle={t(
              "cardOrder.modalAddPurchaseOrder.dropfilePlaceholderTitle"
            )}
          />
        )}
      />
    </div>
  );
};
